/**
 * 消失点/透视网格（M4-07 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 复用透视裁剪（M4-02）的同一套单应矩阵数学：四个角点定义一个"平面"，
 * 平面自带一套局部矩形坐标系（大小取对边平均长，与透视裁剪同一手法）。
 * 与透视裁剪相反的方向——透视裁剪是"文档四边形→矩形输出"（逆采样建新图），
 * 这里是"矩形局部画布→文档四边形"（正向 warp，混合进已有图层）。
 */
import { applyHomography, invertHomography, solveHomography, type Homography, type Point } from './perspective';

export interface VanishingPointPlane {
  /** 文档空间四角点，顺序：左上、右上、右下、左下。 */
  readonly corners: readonly [Point, Point, Point, Point];
  /** 平面局部坐标系的画布大小（对边平均长，四舍五入，≥1）。 */
  readonly planeWidth: number;
  readonly planeHeight: number;
  /** 平面局部坐标 → 文档坐标。 */
  readonly toDocument: Homography;
  /** 文档坐标 → 平面局部坐标。 */
  readonly toPlane: Homography;
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** 四边形（左上/右上/右下/左下）→ 平面局部画布尺寸（对边平均长）。 */
export function planeSizeOf(corners: readonly [Point, Point, Point, Point]): {
  readonly width: number;
  readonly height: number;
} {
  const [tl, tr, br, bl] = corners;
  const width = Math.round((distance(tl, tr) + distance(bl, br)) / 2);
  const height = Math.round((distance(tl, bl) + distance(tr, br)) / 2);
  return { width: Math.max(1, width), height: Math.max(1, height) };
}

/** 由四个文档空间角点建立一个透视平面。 */
export function createVanishingPointPlane(corners: readonly [Point, Point, Point, Point]): VanishingPointPlane {
  const { width, height } = planeSizeOf(corners);
  const rectCorners: readonly [Point, Point, Point, Point] = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];
  const toDocument = solveHomography(rectCorners, corners);
  const toPlane = invertHomography(toDocument);
  return { corners, planeWidth: width, planeHeight: height, toDocument, toPlane };
}

export function planeToDocument(plane: VanishingPointPlane, p: Point): Point {
  return applyHomography(plane.toDocument, p);
}

export function documentToPlane(plane: VanishingPointPlane, p: Point): Point {
  return applyHomography(plane.toPlane, p);
}

/** 平面局部空间里 N 等分的网格线，端点已映射到文档坐标——doc-overlay 直接连线画。 */
export function planeGridLines(
  plane: VanishingPointPlane,
  divisions = 8,
): ReadonlyArray<readonly [Point, Point]> {
  const lines: Array<readonly [Point, Point]> = [];
  for (let i = 0; i <= divisions; i++) {
    const t = i / divisions;
    lines.push([
      planeToDocument(plane, { x: t * plane.planeWidth, y: 0 }),
      planeToDocument(plane, { x: t * plane.planeWidth, y: plane.planeHeight }),
    ]);
    lines.push([
      planeToDocument(plane, { x: 0, y: t * plane.planeHeight }),
      planeToDocument(plane, { x: plane.planeWidth, y: t * plane.planeHeight }),
    ]);
  }
  return lines;
}

/** 四边形在文档空间的整数包围盒（不裁到任何画布尺寸——调用方按需再夹）。 */
export function planeDocumentBounds(
  plane: VanishingPointPlane,
): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } {
  const xs = plane.corners.map((c) => c.x);
  const ys = plane.corners.map((c) => c.y);
  const left = Math.floor(Math.min(...xs));
  const top = Math.floor(Math.min(...ys));
  const right = Math.ceil(Math.max(...xs));
  const bottom = Math.ceil(Math.max(...ys));
  return { x: left, y: top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/**
 * 文档空间一点反查平面局部坐标，双线性采样 patch。落在平面四边形之外
 * 返回 `null`——`warpPatchIntoDocument` 和拖拽期间的实时预览
 * （`VanishingPointToolController#drawOverlay`）共用同一份采样逻辑，
 * 保证"预览＝提交后的真实结果"，不是另开一套近似画法。
 */
export function samplePatchAtDocumentPoint(
  plane: VanishingPointPlane,
  patch: Uint8Array,
  x: number,
  y: number,
): readonly [number, number, number, number] | null {
  const { planeWidth, planeHeight } = plane;
  const local = documentToPlane(plane, { x: x + 0.5, y: y + 0.5 });
  const sx = local.x - 0.5;
  const sy = local.y - 0.5;
  if (sx < -0.5 || sy < -0.5 || sx > planeWidth - 0.5 || sy > planeHeight - 0.5) return null;

  const x0 = Math.max(0, Math.min(planeWidth - 1, Math.floor(sx)));
  const y0 = Math.max(0, Math.min(planeHeight - 1, Math.floor(sy)));
  const x1 = Math.min(planeWidth - 1, x0 + 1);
  const y1 = Math.min(planeHeight - 1, y0 + 1);
  const fx = Math.min(1, Math.max(0, sx - x0));
  const fy = Math.min(1, Math.max(0, sy - y0));

  const sample = (px: number, py: number, c: number): number => patch[(py * planeWidth + px) * 4 + c]!;
  const bilinear = (c: number): number => {
    const top2 = sample(x0, y0, c) * (1 - fx) + sample(x1, y0, c) * fx;
    const bottom2 = sample(x0, y1, c) * (1 - fx) + sample(x1, y1, c) * fx;
    return top2 * (1 - fy) + bottom2 * fy;
  };
  return [bilinear(0), bilinear(1), bilinear(2), bilinear(3)];
}

/**
 * 把一块平面局部空间的 RGBA8 patch（贴图/画笔画的内容，坐标系是
 * `planeWidth × planeHeight`）按该平面的单应矩阵正向 warp，
 * source-over 混合进文档空间的目标缓冲，只处理四边形包围盒内的像素
 * （逆采样：每个目标像素反查平面局部坐标，双线性采样 patch）。
 *
 * @returns 实际写到的文档空间包围盒（`null` 表示四边形与目标缓冲无交集）。
 */
export function warpPatchIntoDocument(
  plane: VanishingPointPlane,
  patch: Uint8Array,
  target: Uint8Array,
  targetWidth: number,
  targetHeight: number,
): { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null {
  const raw = planeDocumentBounds(plane);
  const left = Math.max(0, raw.x);
  const top = Math.max(0, raw.y);
  const right = Math.min(targetWidth, raw.x + raw.width);
  const bottom = Math.min(targetHeight, raw.y + raw.height);
  if (left >= right || top >= bottom) return null;

  for (let y = top; y < bottom; y++) {
    for (let x = left; x < right; x++) {
      const sampled = samplePatchAtDocumentPoint(plane, patch, x, y);
      if (sampled === null) continue;
      const sa = sampled[3] / 255;
      if (sa <= 0) continue;

      const at = (y * targetWidth + x) * 4;
      const da = target[at + 3]! / 255;
      const outA = sa + da * (1 - sa);
      if (outA > 0) {
        for (let c = 0; c < 3; c++) {
          const cs = sampled[c]! / 255;
          const cb = target[at + c]! / 255;
          // patch 是 straight alpha 的普通像素（非叠加模式），B(cb,cs)=cs
          target[at + c] = Math.round((((1 - da) * sa * cs + da * sa * cs + (1 - sa) * da * cb) / outA) * 255);
        }
      }
      target[at + 3] = Math.round(outA * 255);
    }
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}
